import type { Tag } from '~/types/tag'
import { locales } from '@@/shared-components/locales/index'

export const publicTagQuery = ({
  id,
  query,
  thumbs,
  hasPreview,
  placeholderData,
  origin,
  dataLang,
  channel,
}: {
  id: string | number
  query?: object
  thumbs?: {
    [key: string]: object
  }
  hasPreview?: { expires: string; signature: string }
  placeholderData?: { data: Tag }
  origin?: string
  dataLang: Ref<ValueOf<typeof locales>>
  channel: Ref<string | null>
}) =>
  queryOptions({
    queryKey: ['public-tags', dataLang, channel, id],
    queryFn: async (): Promise<{
      data: Tag
    }> =>
      await useCustomFetch<{ data: Tag }>({
        url: `/public/tags/${hasPreview ? 'p/' : ''}${id}`,
        query: { ...query, ...(hasPreview ?? {}) },
        thumbs,
        clientOnly: true,
        options: {
          headers: { 'X-Lang': dataLang.value },
        },
        origin,
      }),

    placeholderData,
  })

export const publicTagsQuery = ({
  query,
  thumbs,
  enabled,
  dataLang,
  channel,
}: {
  query?: Ref<object>
  thumbs?: {
    [key: string]: object
  }
  enabled?: Ref<boolean>
  dataLang: Ref<ValueOf<typeof locales>>
  channel: Ref<string | null>
}) =>
  queryOptions({
    enabled,
    queryKey: ['public-tags', dataLang, /* Kvůli AM*/ channel, query],
    retry: 0,
    queryFn: async ({ signal }): Promise<{ data: Tag[] }> =>
      await useCustomFetch<{ data: Tag[] }>({
        url: '/public/tags',
        query: {
          sort: [{ field: 'position', order: 'asc' }],
          ...query?.value,
        },
        options: {
          headers: { 'X-Lang': dataLang.value },
        },
        thumbs,
        signal,
        clientOnly: true,
      }),
  })

export const tagTypesQuery = ({
  query,
  thumbs,
  dataLang,
}: {
  query?: Ref<object>
  thumbs?: {
    [key: string]: object
  }
  dataLang: Ref<ValueOf<typeof locales>>
}) =>
  queryOptions({
    queryKey: ['tag-types', dataLang, query],
    retry: 0,
    queryFn: async ({ signal }): Promise<{ data: Tag[] }> => {
      return await useCustomFetch<{ data: Tag[] }>({
        url: '/enums/tag-types',
        query: {
          sort: [{ field: 'position', order: 'asc' }],
          ...query?.value,
        },
        options: {
          headers: { 'X-Lang': dataLang.value },
        },
        thumbs,
        signal,
        clientOnly: true,
      })
    },
  })

export const publicTagPairsQuery = ({
  query,
  enabled,
}: { query?: Ref<object>; enabled?: Ref<boolean> } = {}) =>
  queryOptions({
    enabled,
    queryKey: ['public-tag-pairs', query],
    retry: 0,
    queryFn: async ({ signal }): Promise<{ data: Tag[] }> =>
      await useCustomFetch<{ data: Tag[] }>({
        url: '/enums/public-tags',
        query: {
          ...query?.value,
        },
        clientOnly: true,
        signal,
      }),
  })

export const tagsQuery = ({
  query,
  refetchOnMount = 'always',
}: { query?: Ref<object>; refetchOnMount?: 'always' | boolean } = {}) =>
  queryOptions({
    queryKey: ['tags', query],
    retry: 0,
    refetchOnMount,
    queryFn: async ({ signal }): Promise<Tag[]> => {
      const { get } = useApi()
      const res = await get<Tag[]>(
        '/enums/tag-pairs',
        {
          sort: [{ field: 'position', order: 'asc' }],
          ...query?.value,
        },
        { signal }
      )
      return res?.data
    },
  })

export const tagKeysQuery = ({ query }: { query?: Ref<object> } = {}) =>
  queryOptions({
    queryKey: ['tag-keys', query],
    retry: 0,
    queryFn: async ({ signal }): Promise<Tag[]> => {
      const { get } = useApi()
      const res = await get<Tag[]>(
        '/enums/tag-keys',
        {
          ...query?.value,
        },
        { signal }
      )
      return res?.data
    },
  })

export const tagMaxLevelsQuery = ({ query }: { query?: Ref<object> } = {}) =>
  queryOptions({
    queryKey: ['tag-max-levels', query],
    retry: 0,
    queryFn: async ({ signal }): Promise<Tag[]> => {
      const { get } = useApi()
      const res = await get<Tag[]>(
        '/enums/tag-max-levels',
        {
          ...query?.value,
        },
        { signal }
      )
      return res?.data
    },
  })
